import { getCurrentLanguageCode, getIntlLocaleForLanguage } from './language';
import { sanitizeRichTextStyle } from './rich-text-style';
import { API_BASE_URL } from '../config/api';
import {
  PDF_PREVIEW_LIMITS,
  getContentEmbedType,
  getPdfPreviewUrl,
  normalizeContentIframe,
  resolveUploadUrl,
  sanitizePdfPreviewStyle
} from './content-embeds';

const PDF_OPEN_LABELS = { uz: 'PDF ni ochish', ru: 'Открыть PDF', en: 'Open PDF', ko: 'PDF 열기' };

// PDF preview bloklarini bir xil ko'rinishga keltiradi:
// - eski format (<section data-pdf-url data-pdf-height>, ichida iframe yo'q) tiklanadi;
// - manzili yo'qolgan bo'sh bloklar olib tashlanadi;
// - har bir blok ustiga fayl nomi va "PDF ni ochish" tugmasi qo'shiladi (telefon
//   brauzerlari iframe ichida PDF ko'rsatmasa ham, fayl ochiladi).
function enhancePdfPreviews(root, baseUrl) {
  root.querySelectorAll('section.content-pdf-preview').forEach((section) => {
    let frame = section.querySelector('iframe.content-pdf-preview__frame');

    if (!frame) {
      const legacyUrl = getPdfPreviewUrl(section.getAttribute('data-pdf-url') || '', baseUrl);

      if (!legacyUrl) {
        section.remove();
        return;
      }

      const [minHeight, maxHeight] = PDF_PREVIEW_LIMITS.height;
      const legacyHeight = Number.parseInt(section.getAttribute('data-pdf-height') || '', 10);
      const height = Number.isFinite(legacyHeight)
        ? Math.min(Math.max(legacyHeight, minHeight), maxHeight)
        : PDF_PREVIEW_LIMITS.defaultHeight;

      frame = document.createElement('iframe');
      frame.className = 'content-pdf-preview__frame';
      frame.setAttribute('src', legacyUrl);
      frame.setAttribute('loading', 'lazy');
      frame.setAttribute('style', `height: ${height}px`);
      section.appendChild(frame);
    }

    section.removeAttribute('data-pdf-url');
    section.removeAttribute('data-pdf-height');

    const fileName = section.getAttribute('data-file-name') || frame.getAttribute('title') || 'PDF';
    frame.setAttribute('title', fileName);

    if (section.querySelector('.content-pdf-preview__bar')) {
      return;
    }

    const bar = document.createElement('div');
    bar.className = 'content-pdf-preview__bar';

    const name = document.createElement('span');
    name.className = 'content-pdf-preview__name';
    name.textContent = fileName;
    bar.appendChild(name);

    const source = frame.getAttribute('src') || '';

    if (/^https?:\/\//i.test(source)) {
      const open = document.createElement('a');
      open.className = 'content-pdf-preview__open';
      open.href = source;
      open.target = '_blank';
      open.rel = 'noopener noreferrer';
      open.textContent = PDF_OPEN_LABELS[getCurrentLanguageCode()] || PDF_OPEN_LABELS.uz;
      bar.appendChild(open);
    }

    section.insertBefore(bar, section.firstChild);
  });
}

export function formatCmsDate(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat(getIntlLocaleForLanguage(getCurrentLanguageCode()), {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  }).format(date);
}

function htmlToPlainText(html = '') {
  const source = String(html || '');

  if (typeof document !== 'undefined') {
    const decode = (value) => {
      const container = document.createElement('div');
      container.innerHTML = value;

      return String(container.textContent || container.innerText || '');
    };

    const decoded = decode(decode(source));

    return decoded.replace(/<[^>]+>/g, ' ').replace(/\u00a0/g, ' ');
  }

  return source
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/<[^>]+>/g, ' ');
}

export function stripHtml(html = '') {
  return htmlToPlainText(html).replace(/\s+/g, ' ').trim();
}

export function hasMeaningfulLocalizedValue(value) {
  if (value === undefined || value === null) {
    return false;
  }

  if (typeof value !== 'string') {
    return Boolean(value);
  }

  const source = value.trim();

  if (!source) {
    return false;
  }

  if (!source.includes('<')) {
    return Boolean(stripHtml(source));
  }

  if (stripHtml(source)) {
    return true;
  }

  if (/<(img|picture|video|audio|source|svg|canvas|table|hr)\b/i.test(source)) return true;
  if (/class=["'][^"']*content-pdf-preview[^>]*data-pdf-url=/i.test(source)) return true;
  if (/<iframe\b/i.test(source) && typeof document !== 'undefined') {
    const template = document.createElement('template');
    template.innerHTML = source;
    return Array.from(template.content.querySelectorAll('iframe')).some((frame) =>
      getContentEmbedType(frame.getAttribute('src') || '', frame.className));
  }
  return false;
}

function getFirstMeaningfulLocalizedValue(values = [], fallback = '') {
  return values.find(hasMeaningfulLocalizedValue) ?? fallback;
}

export function getHtmlExcerpt(html = '', maxLength = 220) {
  const text = stripHtml(html);

  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength).trimEnd()}...`;
}

export function truncateWords(value = '', maxWords = 20) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();

  if (!text) {
    return '';
  }

  const words = text.split(' ');

  if (words.length <= maxWords) {
    return text;
  }

  return `${words.slice(0, maxWords).join(' ')}...`;
}

function sanitizeUrlValue(value = '') {
  const normalized = String(value || '').trim();

  if (!normalized) {
    return '';
  }

  if (
    normalized.startsWith('/') ||
    normalized.startsWith('./') ||
    normalized.startsWith('../') ||
    normalized.startsWith('#') ||
    normalized.startsWith('?')
  ) {
    return normalized;
  }

  if (/^data:image\/(png|jpe?g|webp|gif);base64,/i.test(normalized)) {
    return normalized;
  }

  if (/^(javascript|vbscript|data:text\/html|data:application\/javascript)/i.test(normalized)) {
    return '';
  }

  if (/^(https?:|mailto:|tel:)/i.test(normalized)) {
    return normalized;
  }

  return normalized;
}

function normalizeFileLinkValue(value = '') {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function decodeFileNameFromHref(value = '') {
  const source = String(value || '').trim();

  if (!source) {
    return '';
  }

  const cleanValue = source.split('#')[0].split('?')[0];
  const fileName = cleanValue.split('/').pop() || '';

  if (!fileName) {
    return '';
  }

  try {
    return decodeURIComponent(fileName);
  } catch {
    return fileName;
  }
}

function markInlineFileLinks(root) {
  root.querySelectorAll('a').forEach((anchor) => {
    if (anchor.classList.contains('attached-files-item')) {
      return;
    }

    const isFileLink =
      anchor.classList.contains('content-file-link') || anchor.hasAttribute('download');

    if (!isFileLink) {
      anchor.classList.remove('content-file-link--file-name');
      return;
    }

    const label = normalizeFileLinkValue(anchor.textContent || '');

    if (!label) {
      anchor.classList.remove('content-file-link--file-name');
      return;
    }

    const candidates = [
      anchor.getAttribute('data-file-name'),
      anchor.getAttribute('download'),
      anchor.getAttribute('title'),
      decodeFileNameFromHref(anchor.getAttribute('href'))
    ]
      .map((value) => normalizeFileLinkValue(value))
      .filter(Boolean);

    if (candidates.includes(label)) {
      anchor.classList.add('content-file-link--file-name');
      return;
    }

    anchor.classList.remove('content-file-link--file-name');
  });
}

export function sanitizeHtml(html = '') {
  const source = String(html || '');

  if (!source) {
    return '';
  }

  if (typeof document !== 'undefined') {
    const template = document.createElement('template');
    template.innerHTML = source;

    template.content
      .querySelectorAll('script, style, object, embed, link, meta, base, form')
      .forEach((node) => node.remove());

    template.content.querySelectorAll('*').forEach((element) => {
      const isIframe = element.tagName === 'IFRAME';
      const embedType = isIframe ? normalizeContentIframe(element, API_BASE_URL) : '';
      const isPdfPreviewFrame = embedType === 'pdf';
      const isPdfPreviewContainer =
        element.tagName === 'SECTION' && element.classList.contains('content-pdf-preview');

      if (isIframe && !embedType) {
        element.remove();
        return;
      }

      Array.from(element.attributes).forEach((attribute) => {
        const name = attribute.name.toLowerCase();
        const value = attribute.value;

        if (name === 'style') {
          const safeStyle = isPdfPreviewFrame
            ? sanitizePdfPreviewStyle(value, 'height')
            : isPdfPreviewContainer
              ? sanitizePdfPreviewStyle(value, 'width')
              : sanitizeRichTextStyle(value);

          if (!safeStyle) {
            element.removeAttribute(attribute.name);
          } else {
            element.setAttribute(attribute.name, safeStyle);
          }
          return;
        }

        if (name.startsWith('on') || ['srcdoc', 'srcset'].includes(name)) {
          element.removeAttribute(attribute.name);
          return;
        }

        if (isPdfPreviewFrame && name === 'src') {
          return;
        }

        if (['href', 'src', 'xlink:href', 'action', 'formaction', 'poster'].includes(name)) {
          const safeValue = sanitizeUrlValue(resolveUploadUrl(value, API_BASE_URL));

          if (!safeValue) {
            element.removeAttribute(attribute.name);
            return;
          }

          if (safeValue !== value) {
            element.setAttribute(attribute.name, safeValue);
          }
        }
      });

      if (element.tagName === 'A' && element.getAttribute('target') === '_blank') {
        element.setAttribute('rel', 'noopener noreferrer');
      }
    });

    enhancePdfPreviews(template.content, API_BASE_URL);
    markInlineFileLinks(template.content);

    return template.innerHTML;
  }

  return source
    .replace(/<\s*(script|style|iframe|object|embed|link|meta|base|form)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/<\s*(script|style|iframe|object|embed|link|meta|base|form)\b[^>]*\/?>/gi, '')
    .replace(/\s+on[a-z-]+\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, '')
    .replace(/\s+(href|src|xlink:href|action|formaction|poster)\s*=\s*("javascript:[^"]*"|'javascript:[^']*'|javascript:[^\s>]+)/gi, '')
    .replace(/\s+srcdoc\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, '');
}

export function createSafeMarkup(html = '', fallback = '') {
  return {
    __html: sanitizeHtml(html || fallback)
  };
}

export function getTranslatedField(item, field, fallback = '', languageCode = getCurrentLanguageCode()) {
  return getFirstMeaningfulLocalizedValue([
    item?.[languageCode]?.[field],
    item?.translations?.[languageCode]?.[field],
    item?.uz?.[field],
    item?.translations?.uz?.[field],
    item?.en?.[field],
    item?.translations?.en?.[field],
    item?.ru?.[field],
    item?.translations?.ru?.[field],
    item?.ko?.[field],
    item?.translations?.ko?.[field]
  ], fallback);
}

export function getLocalizedValue(value = {}, fallback = '') {
  const languageCode = getCurrentLanguageCode();

  return getFirstMeaningfulLocalizedValue([
    value?.[languageCode],
    value?.uz,
    value?.en,
    value?.ru,
    value?.ko
  ], fallback);
}

export function normalizeLocalizedMap(value = {}) {
  return {
    uz: value?.uz || '',
    ru: value?.ru || '',
    en: value?.en || '',
    ko: value?.ko || ''
  };
}

function getContentCreatedTimestamp(item = {}) {
  const createdTimestamp = new Date(item?.createdAt || item?.updatedAt || 0).getTime();

  if (Number.isFinite(createdTimestamp) && createdTimestamp > 0) {
    return createdTimestamp;
  }

  const numericId = Number(item?.id || item?._id || 0);
  return Number.isFinite(numericId) ? numericId : 0;
}

export function sortContentByNewest(items = []) {
  return [...items].sort((left, right) => {
    const timestampDifference =
      getContentCreatedTimestamp(right) - getContentCreatedTimestamp(left);

    if (timestampDifference !== 0) {
      return timestampDifference;
    }

    const rightId = Number(right?.id || right?._id || 0);
    const leftId = Number(left?.id || left?._id || 0);

    if (Number.isFinite(rightId) && Number.isFinite(leftId)) {
      return rightId - leftId;
    }

    return 0;
  });
}

export function normalizeRectorSpeechData(item = {}) {
  return {
    active: item.active ?? true,
    image: item.image || '',
    rectorName: normalizeLocalizedMap(item.rectorName),
    position: normalizeLocalizedMap(item.position),
    title: normalizeLocalizedMap(item.title),
    content: normalizeLocalizedMap(item.content)
  };
}
